import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme, brandingLogoUrl } from "../context/ThemeContext";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import { InstallButton, InstallBanner } from "./InstallPWA";
import {
  LayoutDashboard, Users, Activity, Zap, ClipboardList,
  Upload, LogOut, Bell, ChevronDown, ChevronRight, Menu, X, Play,
  Database, Award, Shield, Sparkles, Dumbbell, HeartPulse, Layers, PieChart,
  Calendar as CalendarIcon, Palette, User as UserIcon, Settings as SettingsIcon,
  LifeBuoy, KeyRound, Target, PanelLeftClose, PanelLeftOpen, BookOpen,
} from "lucide-react";

function photoUrlFor(p) {
  if (!p) return null;
  if (p.startsWith("http")) return p;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/files/${encodeURIComponent(p)}`;
}

/* ---------- Sidebar nav primitives ---------- */

const NavItem = ({ to, icon: Icon, label, tid, onClick, collapsed, nested }) => (
  <NavLink
    to={to}
    end
    onClick={onClick}
    data-testid={tid}
    title={collapsed ? label : undefined}
    className={({ isActive }) =>
      `flex items-center ${collapsed ? "justify-center px-0 mx-2" : `gap-3 ${nested ? "pl-9 pr-3" : "px-3"} mx-2`} py-2.5 text-sm font-medium rounded-lg transition-colors min-h-[40px] ${
        isActive
          ? "bg-[var(--brand-primary)] text-white"
          : "text-[var(--app-text-dim)] hover:text-[var(--app-text)] hover:bg-white/5"
      }`
    }
  >
    <Icon size={16} strokeWidth={2} className="flex-shrink-0" />
    {!collapsed && <span className="text-sm font-medium truncate">{label}</span>}
  </NavLink>
);

/** Collapsible section header (expand/collapse) */
function SectionToggle({ label, open, onToggle, tid, collapsed }) {
  if (collapsed) {
    return (
      <div className="mx-4 my-2 h-px" style={{ background: "var(--app-border)" }} />
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={tid}
      className="w-full flex items-center justify-between px-5 pt-5 pb-2 text-[10px] uppercase tracking-[0.18em] font-bold hover:opacity-80"
      style={{ color: "var(--app-text-faint)" }}
      aria-expanded={open}
    >
      <span>{label}</span>
      <ChevronRight size={12} className="transition-transform" style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }} />
    </button>
  );
}

/** Nested group header inside Management */
function SubGroupToggle({ label, open, onToggle, tid, collapsed }) {
  if (collapsed) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={tid}
      className="w-full flex items-center justify-between px-5 py-1.5 text-[10px] uppercase tracking-[0.14em] font-bold hover:opacity-80"
      style={{ color: "var(--app-text-mute)" }}
      aria-expanded={open}
    >
      <span>{label}</span>
      <ChevronRight size={10} className="transition-transform" style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }} />
    </button>
  );
}

/* ---------- Sidebar body ---------- */

function SidebarNav({ isCoach, isSuperAdmin, onNavigate, collapsed }) {
  // Persist section open state in localStorage so the user's layout survives
  // route changes and refreshes.
  const [progOpen, setProgOpen]   = usePersistedBool("rd.sidebar.programming", true);
  const [mgmtOpen, setMgmtOpen]   = usePersistedBool("rd.sidebar.management",  false);
  const [adminOpen, setAdminOpen] = usePersistedBool("rd.sidebar.admin",       false);
  // Management sub-groups
  const [mgmtResources,   setMgmtResources]   = usePersistedBool("rd.sidebar.mgmt.resources",   true);
  const [mgmtPlanning,    setMgmtPlanning]    = usePersistedBool("rd.sidebar.mgmt.planning",    false);
  const [mgmtData,        setMgmtData]        = usePersistedBool("rd.sidebar.mgmt.data",        false);

  return (
    <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden">
      {/* ===== Primary nav (always visible) ===== */}
      {!collapsed && <div className="px-5 pt-3 pb-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--app-text-faint)" }}>Main</div>}
      <NavItem to="/" icon={LayoutDashboard} label="Dashboard" tid="nav-dashboard" onClick={onNavigate} collapsed={collapsed} />
      {isCoach && <NavItem to="/athletes" icon={Users} label="Athletes" tid="nav-athletes" onClick={onNavigate} collapsed={collapsed} />}
      {isCoach && <NavItem to="/groups"   icon={Users} label="Teams"    tid="nav-groups"   onClick={onNavigate} collapsed={collapsed} />}
      {isCoach && <NavItem to="/testing"  icon={ClipboardList} label="Testing" tid="nav-testing" onClick={onNavigate} collapsed={collapsed} />}

      {/* Athlete-only quick links */}
      {!isCoach && (
        <>
          {!collapsed && <div className="px-5 pt-5 pb-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--app-text-faint)" }}>My Training</div>}
          <NavItem to="/my-workouts"    icon={Play}       label="My Workouts"          tid="nav-my-workouts"    onClick={onNavigate} collapsed={collapsed} />
          <NavItem to="/macro-overview" icon={PieChart}   label="My Macro Plan"        tid="nav-my-macro-plan"  onClick={onNavigate} collapsed={collapsed} />
          <NavItem to="/my-health"      icon={HeartPulse} label="Health Questionnaire" tid="nav-my-health"      onClick={onNavigate} collapsed={collapsed} />
          <NavItem to="/sprint-analysis" icon={Zap}      label="Sprint Analysis"       tid="nav-sprint-analysis" onClick={onNavigate} collapsed={collapsed} />
          <NavItem to="/jump-analysis"   icon={Activity} label="Jump Analysis"         tid="nav-jump-analysis"   onClick={onNavigate} collapsed={collapsed} />
          <NavItem to="/quadrant"        icon={Target}   label="Quadrant"              tid="nav-quadrant"        onClick={onNavigate} collapsed={collapsed} />
        </>
      )}

      {/* ===== Programming (coach-only, expanded by default) ===== */}
      {isCoach && (
        <>
          <SectionToggle label="Programming" open={progOpen} onToggle={() => setProgOpen(!progOpen)} tid="nav-section-programming" collapsed={collapsed} />
          {(progOpen || collapsed) && (
            <div className="space-y-0.5">
              <NavItem to="/programs"         icon={Dumbbell} label="Program Builder"   tid="nav-programs"         onClick={onNavigate} collapsed={collapsed} />
              <NavItem to="/online-programs"  icon={Play}     label="Online Programs"   tid="nav-online-programs"  onClick={onNavigate} collapsed={collapsed} />
              <NavItem to="/athlete-workouts" icon={Layers}   label="Athlete Workouts"  tid="nav-athlete-workouts" onClick={onNavigate} collapsed={collapsed} />
              <NavItem to="/team-workouts"    icon={Users}    label="Team Workouts"     tid="nav-team-workouts"    onClick={onNavigate} collapsed={collapsed} />
            </div>
          )}
        </>
      )}

      {/* ===== Analysis (coach) — always visible, no toggle ===== */}
      {isCoach && (
        <>
          {!collapsed && <div className="px-5 pt-5 pb-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--app-text-faint)" }}>Analysis</div>}
          <NavItem to="/sprint-analysis" icon={Zap}      label="Sprint Analysis" tid="nav-sprint-analysis" onClick={onNavigate} collapsed={collapsed} />
          <NavItem to="/jump-analysis"   icon={Activity} label="Jump Analysis"   tid="nav-jump-analysis"   onClick={onNavigate} collapsed={collapsed} />
          <NavItem to="/quadrant"        icon={Target}   label="Quadrant"        tid="nav-quadrant"        onClick={onNavigate} collapsed={collapsed} />
          <NavItem to="/quadrant-score"  icon={Award}    label="Quadrant Score"  tid="nav-quadrant-score"  onClick={onNavigate} collapsed={collapsed} />
        </>
      )}

      {/* ===== Management (coach-only, collapsed by default, grouped) ===== */}
      {isCoach && (
        <>
          <SectionToggle label="Management" open={mgmtOpen} onToggle={() => setMgmtOpen(!mgmtOpen)} tid="nav-section-management" collapsed={collapsed} />
          {(mgmtOpen || collapsed) && (
            <div className="space-y-0.5">
              {/* Planning sub-group */}
              <SubGroupToggle label="Planning" open={mgmtPlanning} onToggle={() => setMgmtPlanning(!mgmtPlanning)} tid="nav-subgroup-planning" collapsed={collapsed} />
              {(mgmtPlanning || collapsed) && (
                <>
                  <NavItem to="/coach-calendar"  icon={CalendarIcon} label="Calendar"        tid="nav-coach-calendar"  onClick={onNavigate} collapsed={collapsed} nested />
                  <NavItem to="/readiness"       icon={HeartPulse}   label="Readiness"       tid="nav-readiness"       onClick={onNavigate} collapsed={collapsed} nested />
                  <NavItem to="/macro-overview"  icon={PieChart}     label="Macro Overview"  tid="nav-macro"           onClick={onNavigate} collapsed={collapsed} nested />
                  <NavItem to="/macro-templates" icon={Layers}       label="Macro Templates" tid="nav-macro-templates" onClick={onNavigate} collapsed={collapsed} nested />
                </>
              )}
              {/* Resources sub-group */}
              <SubGroupToggle label="Resources" open={mgmtResources} onToggle={() => setMgmtResources(!mgmtResources)} tid="nav-subgroup-resources" collapsed={collapsed} />
              {(mgmtResources || collapsed) && (
                <>
                  <NavItem to="/exercise-library" icon={Dumbbell} label="Exercise Library" tid="nav-exercise-library" onClick={onNavigate} collapsed={collapsed} nested />
                  <NavItem to="/test-library"     icon={Database} label="Test Library"     tid="nav-test-library"     onClick={onNavigate} collapsed={collapsed} nested />
                  <NavItem to="/issue-library"    icon={Database} label="Issue Library"    tid="nav-issue-library"    onClick={onNavigate} collapsed={collapsed} nested />
                  <NavItem to="/standards"        icon={Award}    label="Standards"        tid="nav-standards"        onClick={onNavigate} collapsed={collapsed} nested />
                </>
              )}
              {/* Data sub-group */}
              <SubGroupToggle label="Data" open={mgmtData} onToggle={() => setMgmtData(!mgmtData)} tid="nav-subgroup-data" collapsed={collapsed} />
              {(mgmtData || collapsed) && (
                <>
                  <NavItem to="/health-questions" icon={ClipboardList} label="Intake Forms"     tid="nav-intake-form-builder" onClick={onNavigate} collapsed={collapsed} nested />
                  <NavItem to="/intakes/new"      icon={ClipboardList} label="New Intake"       tid="nav-intake"              onClick={onNavigate} collapsed={collapsed} nested />
                  <NavItem to="/lookup-tables"    icon={Sparkles}      label="Dropdown Options" tid="nav-lookup-tables"       onClick={onNavigate} collapsed={collapsed} nested />
                  <NavItem to="/import"           icon={Upload}        label="Bulk Import"      tid="nav-import"              onClick={onNavigate} collapsed={collapsed} nested />
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* ===== Admin (super admin only) ===== */}
      {isSuperAdmin && (
        <>
          <SectionToggle label="Admin" open={adminOpen} onToggle={() => setAdminOpen(!adminOpen)} tid="nav-section-admin" collapsed={collapsed} />
          {(adminOpen || collapsed) && (
            <div className="space-y-0.5">
              <NavItem to="/admin"   icon={Shield}    label="Control Center" tid="nav-admin"   onClick={onNavigate} collapsed={collapsed} />
              <NavItem to="/pricing" icon={Sparkles}  label="Pricing Plans"  tid="nav-pricing" onClick={onNavigate} collapsed={collapsed} />
            </div>
          )}
        </>
      )}
    </nav>
  );
}

function usePersistedBool(key, def) {
  const [v, setV] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return def;
      return raw === "true";
    } catch { return def; }
  });
  const set = (nv) => {
    setV(nv);
    try { localStorage.setItem(key, String(nv)); } catch {}
  };
  return [v, set];
}

/* ---------- Mobile bottom nav (role-specific, 5 tabs) ---------- */

function BottomNav({ isCoach, user }) {
  const { refreshUser } = useAuth();
  // Login payload lacks athlete_id; /auth/me has it (needed for the Testing tab link).
  useEffect(() => { if (!isCoach && user && !user.athlete_id) refreshUser(); }, [isCoach, user?.athlete_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = isCoach
    ? [
        { to: "/", icon: LayoutDashboard, label: "Home", tid: "mnav-dashboard", end: true },
        { to: "/athletes", icon: Users, label: "Athletes", tid: "mnav-athletes" },
        { to: "/athlete-workouts", icon: Dumbbell, label: "Workouts", tid: "mnav-workouts", also: ["/team-workouts", "/programs", "/online-programs"] },
        { to: "/testing", icon: ClipboardList, label: "Testing", tid: "mnav-testing" },
        { to: "/more", icon: Menu, label: "More", tid: "mnav-more" },
      ]
    : [
        { to: "/", icon: LayoutDashboard, label: "Home", tid: "mnav-dashboard", end: true },
        { to: "/my-workouts", icon: Dumbbell, label: "Workouts", tid: "mnav-workouts" },
        { to: user?.athlete_id ? `/athletes/${user.athlete_id}` : "/sprint-analysis", icon: Activity, label: "Testing", tid: "mnav-testing", also: ["/sprint-analysis", "/jump-analysis", "/quadrant"] },
        { to: "/resources", icon: BookOpen, label: "Resources", tid: "mnav-resources" },
        { to: "/more", icon: Menu, label: "More", tid: "mnav-more" },
      ];
  const { pathname } = useLocation();
  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl border-t pb-[env(safe-area-inset-bottom)]"
      style={{ background: "color-mix(in srgb, var(--app-surface) 92%, transparent)", borderColor: "var(--app-border)" }}
    >
      <div className="flex justify-around px-1">
        {items.map((it) => (
          <NavLink
            key={it.tid}
            to={it.to}
            end={!!it.end}
            data-testid={it.tid}
            className={({ isActive }) => {
              const on = isActive || (it.also || []).some((p) => pathname.startsWith(p));
              return `relative flex flex-col items-center justify-center gap-1 flex-1 min-h-[60px] py-2 text-[11px] font-semibold transition-colors ${
                on ? "text-[var(--brand-accent)]" : "text-[var(--app-text-mute)] active:text-[var(--app-text)]"
              }`;
            }}
          >
            {({ isActive }) => {
              const on = isActive || (it.also || []).some((p) => pathname.startsWith(p));
              return (
                <>
                  <span className={`absolute top-0 h-[3px] w-8 rounded-b-full transition-opacity ${on ? "opacity-100" : "opacity-0"}`} style={{ background: "var(--brand-accent)" }} />
                  <span className={`flex items-center justify-center w-10 h-7 rounded-full transition-colors ${on ? "bg-[color-mix(in_srgb,var(--brand-accent)_16%,transparent)]" : ""}`}>
                    <it.icon size={19} />
                  </span>
                  <span>{it.label}</span>
                </>
              );
            }}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/* ---------- Profile dropdown ---------- */

function ProfileDropdown({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const items = [
    { to: "/profile",             icon: UserIcon,    label: "Your Profile",         tid: "menu-profile" },
    { to: "/settings/branding",   icon: Palette,     label: "Organization Settings",tid: "menu-org" },
    { to: "/settings/appearance", icon: SettingsIcon,label: "Appearance & Theme",   tid: "menu-appearance" },
    { to: "/settings/security",   icon: KeyRound,    label: "Account & Security",   tid: "menu-security" },
    { to: "/help",                icon: LifeBuoy,    label: "Help & Feedback",      tid: "menu-help" },
  ];

  const close = () => setOpen(false);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="topbar-profile-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border transition-colors p-0.5 pr-2 hover:border-[var(--brand-primary)]"
        style={{ background: "var(--app-surface-2)", borderColor: "var(--app-border)" }}
      >
        {user?.photo_url ? (
          <img src={photoUrlFor(user.photo_url)} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-sm text-white"
            style={{ background: "linear-gradient(135deg, var(--brand-primary), #5B21B6)" }}>
            {user?.name?.[0] || "U"}
          </div>
        )}
        <ChevronDown size={14} className="hidden md:block" style={{ color: "var(--app-text-mute)" }} />
      </button>

      {open && (
        <div
          data-testid="profile-menu"
          role="menu"
          className="absolute right-0 top-full mt-2 w-72 rounded-xl shadow-2xl overflow-hidden z-50"
          style={{ background: "var(--app-surface-2)", border: "1px solid var(--app-border-strong)" }}
        >
          {/* Identity header */}
          <div className="px-4 py-4 flex items-center gap-3 border-b" style={{ borderColor: "var(--app-border)" }}>
            {user?.photo_url ? (
              <img src={photoUrlFor(user.photo_url)} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-base text-white"
                style={{ background: "linear-gradient(135deg, var(--brand-primary), #5B21B6)" }}>
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-bold text-sm truncate" style={{ color: "var(--app-text)" }}>{user?.name || "Coach"}</div>
              <div className="text-xs truncate" style={{ color: "var(--app-text-mute)" }}>{user?.email}</div>
            </div>
          </div>

          <div className="py-1">
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                onClick={close}
                data-testid={it.tid}
                role="menuitem"
                className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/5 transition-colors"
                style={{ color: "var(--app-text)" }}
              >
                <it.icon size={16} style={{ color: "var(--app-text-dim)" }} />
                <span>{it.label}</span>
              </Link>
            ))}
          </div>

          <div className="py-1 border-t" style={{ borderColor: "var(--app-border)" }}>
            <button
              type="button"
              onClick={() => { close(); onLogout(); }}
              data-testid="menu-logout"
              role="menuitem"
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/5 transition-colors"
              style={{ color: "var(--accent)" }}
            >
              <LogOut size={16} /> Log Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Layout root ---------- */

export default function Layout() {
  const { user, logout } = useAuth();
  const { effective: brand } = useTheme();
  const customLogo = brandingLogoUrl(brand?.logo_url);
  const navigate = useNavigate();
  const location = useLocation();
  const isCoach = user && user.role !== "athlete";
  const isSuperAdmin = user && user.role === "super_admin";
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = usePersistedBool("rd.sidebar.collapsed", false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  const closeDrawer = () => setDrawerOpen(false);
  const handleLogout = async () => { await logout(); navigate("/login"); };

  const LogoBlock = (
    <div className={`${collapsed ? "px-3 py-5" : "px-5 py-6"} border-b flex items-center ${collapsed ? "justify-center" : ""}`} style={{ borderColor: "var(--app-border)" }}>
      {customLogo ? (
        <img
          src={customLogo}
          alt="Workspace logo"
          className={`${collapsed ? "w-10 h-10 object-contain" : "h-10 max-w-[180px] object-contain"}`}
          data-testid="brand-logo-custom"
        />
      ) : collapsed ? (
        <img src="/rd-logo.jpeg" alt="RD SmartMetrics" className="w-10 h-10 rounded-md object-cover" data-testid="brand-logo" />
      ) : (
        <div className="flex items-center gap-3">
          <img src="/rd-logo.jpeg" alt="RD SmartMetrics" className="w-11 h-11 rounded-md object-cover flex-shrink-0" data-testid="brand-logo" />
          <div>
            <div className="font-heading text-base font-black uppercase tracking-tight leading-none" style={{ color: "var(--app-text)" }}>RD SmartMetrics</div>
            <div className="text-[10px] uppercase tracking-[0.16em] mt-1" style={{ color: "var(--app-text-mute)" }}>Work Smart › Work Hard</div>
          </div>
        </div>
      )}
    </div>
  );

  const CollapseToggle = (
    <button
      type="button"
      data-testid="sidebar-collapse-toggle"
      onClick={() => setCollapsed(!collapsed)}
      className={`border-t w-full flex items-center ${collapsed ? "justify-center" : "gap-2 px-5"} py-3 text-xs uppercase tracking-widest font-bold hover:bg-white/5 transition-colors`}
      style={{ borderColor: "var(--app-border)", color: "var(--app-text-dim)" }}
      title={collapsed ? "Expand menu" : "Collapse menu"}
    >
      {collapsed ? <PanelLeftOpen size={16} /> : (<><PanelLeftClose size={14} /> Collapse menu</>)}
    </button>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--app-bg)", color: "var(--app-text)" }}>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex ${collapsed ? "w-16" : "w-60"} border-r flex-col transition-[width] duration-200`}
        style={{ background: "var(--app-surface)", borderColor: "var(--app-border)" }}
        data-testid="sidebar"
      >
        {LogoBlock}
        <SidebarNav isCoach={isCoach} isSuperAdmin={isSuperAdmin} collapsed={collapsed} />
        {CollapseToggle}
      </aside>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/70" onClick={closeDrawer} data-testid="mobile-drawer-backdrop" />
          <aside className="relative w-72 max-w-[85vw] flex flex-col animate-slide-in-left"
            style={{ background: "var(--app-surface)", borderRight: "1px solid var(--app-border)" }}>
            <div className="flex items-center justify-between border-b" style={{ borderColor: "var(--app-border)" }}>
              <div className="flex-1">{LogoBlock}</div>
              <button
                onClick={closeDrawer}
                data-testid="mobile-drawer-close"
                aria-label="Close menu"
                className="w-12 h-12 flex items-center justify-center hover:opacity-80 mr-1"
                style={{ color: "var(--app-text-dim)" }}
              >
                <X size={22} />
              </button>
            </div>
            <SidebarNav isCoach={isCoach} isSuperAdmin={isSuperAdmin} onNavigate={closeDrawer} collapsed={false} />
          </aside>
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header
          className="h-14 md:h-16 border-b flex items-center justify-between px-3 md:px-6 pt-[env(safe-area-inset-top)]"
          style={{ background: "var(--app-bg)", borderColor: "var(--app-border)" }}
          data-testid="top-header"
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setDrawerOpen(true)}
              className="md:hidden w-11 h-11 flex items-center justify-center -ml-2 hover:bg-white/5 rounded-lg"
              style={{ color: "var(--app-text)" }}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            {/* Mobile brand */}
            <div className="md:hidden flex items-center gap-2 min-w-0">
              <img src="/rd-logo.jpeg" alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
              <span className="font-heading font-black uppercase tracking-tight text-sm truncate" style={{ color: "var(--app-text)" }}>RD SmartMetrics</span>
            </div>
            {/* Desktop org pill */}
            <div
              className="hidden md:flex rounded-lg px-3 py-1.5 items-center gap-2 text-sm"
              style={{ background: "var(--app-surface-2)", border: "1px solid var(--app-border)", color: "var(--app-text)" }}
              data-testid="org-pill"
            >
              <img src="/rd-logo.jpeg" alt="" className="w-5 h-5 rounded object-cover" />
              <span className="font-bold">{user?.organization || "RD SmartMetrics"}</span>
              <ChevronDown size={14} style={{ color: "var(--app-text-mute)" }} />
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <div className="hidden md:block"><WorkspaceSwitcher /></div>
            <InstallButton />
            <button
              data-testid="notification-btn"
              className="relative w-10 h-10 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-colors"
              style={{ background: "var(--app-surface-2)", border: "1px solid var(--app-border)", color: "var(--app-text)" }}
              aria-label="Notifications"
            >
              <Bell size={15} />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full" style={{ background: "var(--accent)" }}></span>
            </button>
            <Link
              to="/help"
              data-testid="header-help-link"
              className="hidden md:flex w-9 h-9 rounded-full items-center justify-center transition-colors"
              style={{ background: "var(--app-surface-2)", border: "1px solid var(--app-border)", color: "var(--app-text)" }}
              aria-label="Help"
              title="Help & Feedback"
            >
              <LifeBuoy size={15} />
            </Link>
            <ProfileDropdown user={user} onLogout={handleLogout} />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-24 md:pb-0" data-testid="main-content">
          <InstallBanner />
          <Outlet />
        </main>
      </div>

      <BottomNav isCoach={isCoach} user={user} />
    </div>
  );
}
